import { WINDOWS_1250_CHARS } from '../constants';
import { ExportEncoding } from '../types';
import stripDiacritics from './stripDiacritics';

const isAscii = (value: string): boolean =>
  Array.from(value).every((char) => char.charCodeAt(0) <= 0x7f);

const toAsciiChars = (char: string): string => {
  if (char.charCodeAt(0) <= 0x7f) return char;

  const stripped = stripDiacritics(char);
  // An empty result means the character was itself a diacritic, so it is dropped rather than degraded.
  if (!stripped) return '';
  if (!isAscii(stripped)) return '?';

  return stripped;
};

// Transliteration is not 1:1, so the ASCII string is built first and sized afterwards.
const encodeAscii = (content: string): Uint8Array => {
  const ascii = Array.from(content).map(toAsciiChars).join('');
  const bytes = new Uint8Array(ascii.length);
  for (let index = 0; index < ascii.length; index += 1) {
    bytes[index] = ascii.charCodeAt(index);
  }

  return bytes;
};

const encodeText = (content: string, encoding: ExportEncoding): Uint8Array => {
  if (encoding === 'utf-8') {
    return new TextEncoder().encode(content);
  }

  if (encoding === 'ascii') {
    return encodeAscii(content);
  }

  // Windows-1250 is needed for legacy exports; glyphs outside the codepage degrade explicitly to '?'.
  // NFC first: the table is keyed by precomposed code points, so decomposed input would miss every entry.
  const chars = Array.from(content.normalize('NFC'));
  const bytes = new Uint8Array(chars.length);
  chars.forEach((char, index) => {
    const charCode = char.charCodeAt(0);
    bytes[index] =
      charCode <= 0x7f ? charCode : (WINDOWS_1250_CHARS[char] ?? 0x3f);
  });

  return bytes;
};

export default encodeText;
